import React from 'react';

import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';

import type { CsatFormComponentConfig, FlowComponentRenderProps } from '../types';
import sentimentExcited from '../../assets/images/sentiment-excited-v2.svg';
import sentimentNeutral from '../../assets/images/sentiment-neutral-v2.svg';
import sentimentSatisfied from '../../assets/images/sentiment-satisfied-v2.svg';
import sentimentVerySatisfied from '../../assets/images/sentiment-very-satisfied-v2.svg';
import sentimentVeryDissatisfied from '../../assets/images/sentiment-very-dissatisfied-v2.svg';
import HtmlText from '../components/HtmlText';
import InlineFeedback from '../components/rating/InlineFeedback';
import FeedbackRatingSubmitArea from './FeedbackRatingSubmitArea';
import {
  buildRatingValue,
  defaultButtonStyles,
  getRatingState,
  selectedButtonStyles,
} from '../components/rating/ratingUtils';

const csatFaceIconByValue: Record<number, string> = {
  1: sentimentVeryDissatisfied,
  2: sentimentNeutral,
  3: sentimentSatisfied,
  4: sentimentVerySatisfied,
  5: sentimentExcited,
};

/** Keep the selected CSAT sentiment visually identical in the control and downstream summary. */
export function FeedbackCsatFaceIcon({
  value,
  size,
  label,
}: {
  value: number;
  size: number;
  label?: string;
}) {
  const faceIcon = csatFaceIconByValue[value] ?? sentimentSatisfied;

  return (
    <Box
      aria-hidden={label ? undefined : true}
      aria-label={label}
      role={label ? 'img' : undefined}
      sx={{
        alignItems: 'center',
        display: 'flex',
        height: size,
        justifyContent: 'center',
        width: size,
      }}
    >
      <Box
        // Native style preserves escaped quotes in bundled SVG data URLs.
        style={{
          maskImage: `url(${JSON.stringify(faceIcon)})`,
          WebkitMaskImage: `url(${JSON.stringify(faceIcon)})`,
        }}
        data-testid={`csat-face-${value}`}
        sx={{
          width: `${size * (5 / 6)}px`,
          height: `${size * (5 / 6)}px`,
          bgcolor: 'currentColor',
          maskSize: 'contain',
          maskRepeat: 'no-repeat',
          maskPosition: 'center',
        }}
      />
    </Box>
  );
}

const FeedbackCsatRating: React.FC<FlowComponentRenderProps<CsatFormComponentConfig>> = ({
  config,
  goNext,
  setValue,
  submitValueAndGoNext,
  value,
  pending,
}) => {
  const ratingState = getRatingState(value);
  const selectedValue = ratingState.score;
  const submitted = ratingState.submitted;
  const selectionLocked = submitted || pending === true;
  const layout = config.layout ?? 'row';
  const buttonStyle = config.style ?? 'faces_labels';
  const colorCoding = config.colorCoding === true;
  const options = config.options;
  const wrapChoices = layout === 'row' && options.length > 4;
  const optionColor = (value: number) =>
    value <= 2
      ? (config.negativeColor ?? '#FECACA')
      : value === 3
        ? (config.neutralColor ?? '#FEF3C7')
        : (config.positiveColor ?? '#DCFCE7');

  return (
    <Card
      sx={{
        bgcolor: 'transparent',
        border: 0,
        borderRadius: 0,
        boxShadow: 'none',
        color: 'var(--cx-brand-text)',
        p: 0,
      }}
    >
      <Stack spacing={0}>
        <HtmlText
          component="div"
          html={config.question ?? 'Rate your experience'}
          variant="subtitle2"
          sx={{
            '&&': { mb: 1.25 },
            fontSize: 16,
            fontWeight: 600,
            lineHeight: '24px',
            textAlign: 'center',
          }}
        />
        <Box
          sx={{
            mb: 2,
            display: wrapChoices ? 'flex' : 'grid',
            flexWrap: 'wrap',
            justifyContent: 'center',
            gap: 1,
            gridTemplateColumns:
              layout === 'vertical'
                ? '1fr'
                : layout === 'grid'
                  ? 'repeat(2, minmax(0, 1fr))'
                  : `repeat(${options.length}, minmax(0, 1fr))`,
          }}
        >
          {options.map((option) => {
            const selected = selectedValue === option.value;
            const faceIconSize = buttonStyle === 'compact' ? 24 : 34;

            return (
              <Button
                aria-label={option.label}
                aria-pressed={selected}
                disabled={selectionLocked}
                key={option.value}
                onClick={() =>
                  {
                    if (selectionLocked) return;
                    setValue(config.id, buildRatingValue(config, ratingState, option.value, false));
                    submitValueAndGoNext(config.id, buildRatingValue(config, ratingState, option.value, true));
                  }
                }
                sx={{
                  ...(selected ? selectedButtonStyles : defaultButtonStyles),
                  bgcolor: selected
                    ? 'var(--cx-brand-button-selected-bg)'
                    : colorCoding
                      ? optionColor(option.value)
                      : 'var(--cx-brand-button-bg)',
                  color: selected
                    ? 'var(--cx-brand-button-selected-text)'
                    : colorCoding
                      ? '#1C252E'
                      : 'var(--cx-brand-button-text)',
                  border: 0,
                  borderRadius: '6px',
                  textTransform: 'none',
                  '@media (max-width:360px)': { px: 0.5 },
                  flexDirection:
                    layout === 'vertical' || buttonStyle === 'compact' ? 'row' : 'column',
                  fontWeight: 600,
                  gap: 0.75,
                  height: 'auto',
                  minHeight: buttonStyle === 'compact' ? 48 : 86,
                  flex: wrapChoices ? '0 0 calc((100% - 16px) / 3)' : undefined,
                  minWidth: wrapChoices ? 'min(100%, 96px)' : 0,
                  overflowWrap: 'anywhere',
                  px: 1.5,
                  boxShadow: selected
                    ? '0 8px 8px rgba(145,158,171,0.16)'
                    : '0 8px 16px rgba(145,158,171,0.16)',
                  transition: selected ? 'none' : undefined,
                  '&:hover': {
                    bgcolor: selected
                      ? 'var(--cx-brand-button-selected-bg)'
                      : colorCoding
                        ? optionColor(option.value)
                        : 'var(--cx-brand-button-hover-bg)',
                    border: 0,
                    color: selected
                      ? 'var(--cx-brand-button-selected-text)'
                      : colorCoding
                        ? '#1C252E'
                        : 'var(--cx-brand-button-text)',
                  },
                  '&.Mui-focusVisible': {
                    outline: '2px solid var(--cx-brand-primary)',
                    outlineOffset: '2px',
                  },
                  '&:disabled': {
                    color: selected
                      ? 'var(--cx-brand-button-selected-text)'
                      : 'var(--cx-brand-text-muted)',
                    opacity: 0.75,
                  },
                }}
                type="button"
                variant="text"
              >
                {buttonStyle !== 'labels' ? (
                  <FeedbackCsatFaceIcon value={option.value} size={faceIconSize} />
                ) : null}
                {buttonStyle !== 'faces' ? (
                  <Typography
                    component="span"
                    sx={{
                      color: 'inherit',
                      fontSize: 14,
                      fontWeight: 600,
                      lineHeight: '22px',
                    }}
                  >
                    {option.label}
                  </Typography>
                ) : null}
              </Button>
            );
          })}
        </Box>
        <Box sx={{ mb: config.feedbackMode === 'inline' ? 2 : 0 }}>
          <InlineFeedback
            config={config}
            locked={selectionLocked}
            ratingState={ratingState}
            setValue={setValue}
          />
        </Box>
        {submitted && <FeedbackRatingSubmitArea
          config={config}
          continueToNext={goNext}
          onEdit={() =>
            setValue(config.id, {
              ...ratingState,
              submitted: false,
            })
          }
          ratingState={ratingState}
          selectedValue={selectedValue}
          submitted={submitted}
          submitValueAndGoNext={submitValueAndGoNext}
        />}
      </Stack>
    </Card>
  );
};

export default FeedbackCsatRating;
